import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import useScript from '@charlietango/use-script';
import DateFnsUtils from '@date-io/date-fns';
import {
  CssBaseline,
  MuiThemeProvider,
  StylesProvider,
} from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import * as Sentry from '@sentry/react';
import 'assets/fonts/icomoon.css';
import { useObservableCallback } from 'observable-hooks';
import 'pure-react-carousel/dist/react-carousel.es.css';
import { useEffect, useMemo, useState } from 'react';
import { isIOS } from 'react-device-detect';
import {
  Provider as ReduxProvider,
  useDispatch,
  useSelector,
} from 'react-redux';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { Router } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { merge } from 'rxjs';
import SimpleReactLightbox from 'simple-react-lightbox';
import styled, { css, ThemeProvider } from 'styled-components/macro';
import * as serviceWorkerRegistration from '../service-worker-reg';
import BusinessApp from './business-app/BusinessApp';
import ConsumerApp from './consumer-app/ConsumerApp';
import { FETCHIFY_SCRIPT_PATH } from './core/api/api-core/api-constants';
import { appEnv } from './core/environment';
import {
  getErrorMsg,
  logSentryOnNative,
} from './core/errors/error-utils';
import {
  InAppNotifications,
  initNotifications,
} from './core/notifications/fcm-service';
import UpdateHandler from './core/notifications/use-update-handler';
import { persistor, store } from './core/redux/configure-store';
import { contextActions } from './core/redux/context-redux';
import AppSnackbar, {
  AppSnackbarOpts,
} from './shared/components/dialog/AppSnackbar';
import BasicDialog, {
  BasicDialogOpts,
  InstructionDialogOpts,
  useInstructionToBasicDialog,
} from './shared/components/dialog/BasicDialog';
import { StylePageBody } from './shared/components/misc';
import NetworkMonitor from './shared/components/utilities/NetworkMonitor';
import { FramePortalProvider } from './shared/context-hooks/use-frame-portal.hook';
import { InstructionsProvider } from './shared/context-hooks/use-instructions.hook';
import { ShowInfoProvider } from './shared/context-hooks/use-show-info.hook';
import { routerHistory } from './shared/frames/AppRoute';
import AppUrlListener from './shared/pages/AppUrlListener';
import ErrorScreen from './shared/pages/ErrorScreen';
import theme from './styles/theme';
import {
  useInitialiseScrollMemory,
  useObservableSend,
} from './tools/hooks/misc-hooks';

const THUMBS_HEIGHT = 60;
const PAD_TOP = 20;
const PAD_BOTTOM = 40;
const StyleLightbox = styled(StylePageBody)`
  ${isIOS &&
  css`
    --vh: 1vh !important;
    @media (max-width: 768px) {
      .SRLContainer {
        padding-top: ${PAD_TOP}px;
        padding-bottom: ${PAD_BOTTOM}px;
      }
      .SRLElementContainer {
        height: calc(
          (var(--vh, 1vh) * 100) -
            ${THUMBS_HEIGHT + PAD_TOP + PAD_BOTTOM}px
        );
      }
    }
  `}
`;

if (Capacitor.isNativePlatform()) {
  StatusBar.setStyle({ style: Style.Light }).catch(logSentryOnNative);
}

function AppInner(): JSX.Element {
  useInitialiseScrollMemory();
  const dispatch = useDispatch();
  const appOnline = !!useSelector(s => s.context.appOnline);

  const [waitingWorker, setWaitingWorker] =
    useState<ServiceWorker | null>(null);

  const [showSnack, snack$] = useObservableSend<AppSnackbarOpts>();
  const [showDialog, dialog$] = useObservableSend<BasicDialogOpts>();
  const [showInstruction, instruction$] = useObservableCallback<
    BasicDialogOpts,
    InstructionDialogOpts
  >(useInstructionToBasicDialog());

  const showInfoFunctions = useMemo(
    () => ({
      showSnack,
      showDialog,
      showInstruction,
    }),
    [showDialog, showInstruction, showSnack]
  );

  useEffect(() => {
    serviceWorkerRegistration.register({
      onUpdate: worker => setWaitingWorker(worker.waiting),
    });
  }, []);

  useEffect(() => {
    if (appOnline) {
      initNotifications(err => {
        const errMsg = getErrorMsg(err) || '';
        const joiner = errMsg ? ': ' : '';
        showSnack({
          message: `Notifications service error${joiner}${errMsg}`,
          alertType: 'error',
        });
      });
    }
  }, [appOnline, showSnack]);

  const [fetchifyLoaded] = useScript(FETCHIFY_SCRIPT_PATH);
  useEffect(() => {
    dispatch(contextActions.fetchifyLoaded.set(fetchifyLoaded));
  }, [dispatch, fetchifyLoaded]);

  return (
    <ShowInfoProvider {...{ showInfoFunctions }}>
      {appEnv.APP_VARIANT === 'consumer' ? (
        <ConsumerApp />
      ) : (
        <BusinessApp />
      )}
      <AppSnackbar {...{ snack$ }} />
      <BasicDialog dialog$={merge(dialog$, instruction$)} />
      <NetworkMonitor />
      <InAppNotifications />
      <AppUrlListener />
      <UpdateHandler {...{ waitingWorker }} />
      <StyleLightbox />
    </ShowInfoProvider>
  );
}

export default function App(): JSX.Element {
  return (
    <Sentry.ErrorBoundary fallback={ErrorScreen}>
      <MuiThemeProvider {...{ theme }}>
        <ThemeProvider {...{ theme }}>
          <CssBaseline />
          <StylesProvider injectFirst>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <Router history={routerHistory}>
                <ReduxProvider {...{ store }}>
                  <PersistGate loading={null} persistor={persistor}>
                    <FramePortalProvider>
                      <InstructionsProvider>
                        <SimpleReactLightbox>
                          <AppInner />
                        </SimpleReactLightbox>
                      </InstructionsProvider>
                    </FramePortalProvider>
                  </PersistGate>
                </ReduxProvider>
              </Router>
            </MuiPickersUtilsProvider>
          </StylesProvider>
        </ThemeProvider>
      </MuiThemeProvider>
    </Sentry.ErrorBoundary>
  );
}
